import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect
} from "react";
import { useSidebarMenuSelectionStore } from "./sidebarMenuSelectionStore";
import { SidebarMenu } from "./SidebarMenu";
import { NoResults } from "client/Components";
import {
	GlobalNavigationSidebar
} from "client/Features/Layout/GlobalNavigationSidebar/GlobalNavigationSidebar";
import { SubNavigation } from "client/Features/Layout/GlobalNavigationSidebar/SubNavigation";
import { useHandleNavigationInGlobalSubNavigation } from "client/Features/Layout/GlobalNavigationSidebar/useGlobalNavigationVisibilityState";
import { useSidebarMenuStore } from "./sidebarMenuStore";
import { shallowComparison } from "client/Misc/compareUtils";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";

export const SettingsSidebar = (): ReactElement => {
	const {
		model,
		status,
		fetch
	} = useSidebarMenuStore(
		x => ({
			model: x.state,
			status: x.status,
			fetch: x.fetch
		}),
		shallowComparison
	);

	const alertManager: AlertManager = useAlerts();

	const handleNavigationInGlobalSubNavigation: () => void = useHandleNavigationInGlobalSubNavigation();

	const loadingErrorHandler = useCallback(
		(): void => {
			alertManager.showError(localize("_GeneralErrorMessage"));
		},
		[alertManager]
	);

	useEffect(
		(): void => {
			fetch({
				onError: loadingErrorHandler,
			});
		},
		[fetch, loadingErrorHandler]
	);

	const selectedItemId: SettingsPageKind | undefined = useSidebarMenuSelectionStore(x => x.selectedItemId);

	const renderContent = (shouldRenderSkeleton: boolean): ReactElement => {
		if (shouldRenderSkeleton
			&& !model
			&& status === "loading"
		) {
			return (
				<SubNavigation.LoadingIndicator />
			);
		}

		if (status === "error") {
			return (
				<NoResults
					title={localize("_UnableToRetrieveItems")}
					icon="gear"
					message={localize("_WeCantRetrieveAnyItemsAtThisMomentPleaseRetry")}
					onRetry={
						() => fetch({
							forceReload: true,
							onError: loadingErrorHandler
						})
					}
					height="fill"
				/>
			);
		}

		return (
			<SidebarMenu
				model={model}
				onSidebarNavigation={handleNavigationInGlobalSubNavigation}
				selectedItemId={status === "ready" ? selectedItemId : undefined}
			/>
		);
	};

	return (
		<GlobalNavigationSidebar.ActivePanelOverride
			kind="settings"
			title={localize("_Settings")}
		>
			{renderContent(true)}
		</GlobalNavigationSidebar.ActivePanelOverride>
	);
};
SettingsSidebar.displayName = "SettingsSidebar";